import { ApiService } from "../../../config";
import { handleToken } from "../../../utils/expiredTokenLogout";
import { handleError } from "../../../utils/parseApiReturn";
import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

interface GetProfilesParams {
  profileStatus?: string;
  ranking?: string;
  localidade?: string;
  query?: string;
  page?: number;
  size?: number;
  sort?: string;
  /** "somente" = só fichas marcadas como duplicata (cópia) no Google. */
  copias?: string;
  /** "sem-no-google" = só fichas sem botão de WhatsApp no Google. */
  whatsapp?: string;
  /** "somente" = só fichas sem acesso no Google (o padrão as esconde). */
  acesso?: string;
  token: string;
}

export interface ResultadoReconferencia {
  resultado: "LIBERADO" | "SEGUE_COPIA" | "INACESSIVEL" | "NAO_ERA_COPIA";
  /** true enquanto a ficha continuar sem controle (cópia ou inacessível). */
  copia: boolean;
  mensagem: string;
}

/** Reconfere agora, no Google, uma ficha marcada como cópia. */
const reconferirCopia = async (
  profileBusinessId: string,
  token: string,
): Promise<ResultadoReconferencia | null> => {
  const result = await ApiService.post(
    `/business-profiles/${profileBusinessId}/reconferir-copia`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const { data, error, message } = result.data;
  if (error) {
    handleToken(message);
    throw new Error(message);
  }
  return { ...data, mensagem: message };
};

interface UpdateProfileParams {
  profileBusinessId: string;
  razaoSocial?: string;
  locatidade?: string;
  email?: string;
  profileStatus?: string;
  token: string;
}

const getFollowingProfiles = async (
  params: GetProfilesParams
): Promise<any> => {
  try {
    const { token, ...queryParams } = params;
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: queryParams,
    };

    const result = await ApiService.get("/customers/tracking", config);
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      const { message } = handleError(
        error,
        "Erro ao obter perfis em acompanhamento."
      );
      toast.error(message);
      return null;
    }
    return data;
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro inesperado ao obter perfis em acompanhamento."
    );
    toast.error(message);
    return null;
  }
};

const updateFollowingProfile = async (
  params: UpdateProfileParams
): Promise<any> => {
  console.log('updateFollowingProfile called with params:', params);
  try {
    const { token, profileBusinessId, ...updateData } = params;
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const promises = [];

    if (updateData.razaoSocial || updateData.profileStatus) {
      const payload = {
        ...(updateData.razaoSocial ? { name: updateData.razaoSocial } : {}),
        ...(updateData.profileStatus
          ? { workStatus: updateData.profileStatus }
          : {}),
      };
      console.log('Sending PUT to /general with:', payload);
      promises.push(
        ApiService.axiosInstance.put(
          `/business-profiles/${profileBusinessId}/general`,
          payload,
          config
        ).then(res => {
          console.log('/general response:', res?.data);
          return res;
        }).catch(err => {
          console.error('/general error:', err);
          throw err;
        })
      );
    }

    // 2. Location (Address)
    if (updateData.locatidade) {
      console.log('Sending PUT to /location with:', { completeAddress: updateData.locatidade });
      promises.push(
        ApiService.axiosInstance.put(
          `/business-profiles/${profileBusinessId}/location`,
          {
            completeAddress: updateData.locatidade,
          },
          config
        ).then(res => {
          console.log('/location response:', res?.data);
          return res;
        }).catch(err => {
          console.error('/location error:', err);
          throw err;
        })
      );
    }

    // 3. Contact (Email)
    if (updateData.email) {
      console.log('Sending PUT to /contact with:', { email: updateData.email });
      promises.push(
        ApiService.axiosInstance.put(
          `/business-profiles/${profileBusinessId}/contact`,
          {
            email: updateData.email,
          },
          config
        ).then(res => {
          console.log('/contact response:', res?.data);
          return res;
        }).catch(err => {
          console.error('/contact error:', err);
          throw err;
        })
      );
    }

    const results = await Promise.all(promises);
    console.log('All promises resolved:', results);

    toast.success("Perfil atualizado com sucesso!");
    // Return data from first successful request or null
    return results.length > 0 ? results[0].data.data : null;
  } catch (error) {
    const { message } = handleError(error, "Erro ao atualizar perfil.");
    toast.error(message);
    throw error;
  }
};

const toggleTracking = async (
  profileBusinessId: number,
  token: string
): Promise<any> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const result = await ApiService.post(
      `/business-profiles/${profileBusinessId}/changeTracking`,
      {},
      config
    );

    toast.success("Status de acompanhamento alterado!");
    return result.data.data;
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro ao alterar status de acompanhamento."
    );
    toast.error(message);
    throw error;
  }
};

export const FollowingProfilesService = {
  getFollowingProfiles,
  updateFollowingProfile,
  toggleTracking,
  reconferirCopia,
};
